import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../../utils/api";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@mui/material";
import ModalPopup from "../../components/ModalPopup";
import RedemptionForm from "./Forms/RedemptionForm";
import { useNavigate } from "react-router-dom";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { DATA_GRID_STYLES } from "../../components/BasicUserInfo";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { COLORS } from "../../utils/colors";
import ModalImage from "react-modal-image";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import MenuItem from "@mui/material/MenuItem";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { showErrorToast, showSuccessToast, showWarningToast } from "../../utils/toast";
import {
  XeroContactMismatchAlert,
  XeroContactResolveDialog,
} from "../../components/XeroContactMismatch";
import { isAdministratorPortal } from "../../components/XeroIntegrationHealth";
import {
  isXeroRateLimitMessage,
  parseFutureTimestamp,
} from "../../utils/xeroRateLimitUi";
import RedemptionVerificationPanel from "./RedemptionVerificationPanel";
import { resolveBillingVerificationStatus } from "../../utils/nextRedemptionBillingRun";

const VALID_VEHICLE_TYPES = ["sedan", "wagon", "4wd"];

function verifyFailureMessage(response, error) {
  const raw =
    response?.data?.message ||
    error?.response?.data?.message ||
    error?.message ||
    "";
  const dropped =
    !response ||
    response.status === 0 ||
    response.isNetworkError ||
    error?.code === "ECONNABORTED" ||
    /^network error$/i.test(String(raw).trim());
  if (dropped) {
    return "Verification did not finish because it took too long, usually while waiting on Xero. Refresh this redemption before trying again. A bill may already have been created.";
  }
  return raw || "Failed to verify this redemption. Please try again.";
}

function xeroCallBannerMessage(status) {
  if (!status || status.inFlight <= 0) return "";
  if (status.atConcurrentLimit) {
    return `Xero is already handling ${status.concurrentLimit} requests. Wait before verifying so we stay inside the rate limit.`;
  }
  const running = status.inFlight === 1 ? "A Xero request is" : `${status.inFlight} Xero requests are`;
  return `${running} still running (${status.inFlight} of ${status.concurrentLimit}).`;
}

const RedemptionSingle = () => {
  let navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [editRedemptionState, setEditRedemptionState] = useState(false);

  const [selectedRedemption, setSelectedRedemption] = useState(null);
  const [deleteConfirmationIsOpen, setDeleteConfirmationIsOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [duplicateGuardOverrideReason, setDuplicateGuardOverrideReason] = useState("");
  const [billingPreview, setBillingPreview] = useState(null);
  const [billingPreviewInitialLoading, setBillingPreviewInitialLoading] = useState(false);
  const [billingPreviewTotalsRefreshing, setBillingPreviewTotalsRefreshing] = useState(false);
  const [billingPreviewError, setBillingPreviewError] = useState(null);
  const verifyPreviewHasLoadedRef = useRef(false);
  const [billingPreOverride, setBillingPreOverride] = useState("");
  const [billingVehicleTypeOverride, setBillingVehicleTypeOverride] = useState("");
  const [isVerifiedLoading, setIsVerifiedLoading] = useState(false);
  const [xeroCallStatus, setXeroCallStatus] = useState(null);
  const [uploadImageModalOpen, setUploadImageModalOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const fileInputRef = useRef(null);
  const [xeroResolveOpen, setXeroResolveOpen] = useState(false);
  const [pageXeroCheck, setPageXeroCheck] = useState(null);
  const [pageXeroCheckLoading, setPageXeroCheckLoading] = useState(false);
  const [invoiceRefreshLoading, setInvoiceRefreshLoading] = useState(false);

  const refreshInvoiceFromXero = async () => {
    if (!selectedRedemption?._id || !selectedRedemption?.invoiceID) return;
    setInvoiceRefreshLoading(true);
    try {
      const response = await api(
        `v1/redemption/${selectedRedemption._id}/invoiceDetails?refresh=1`,
        "GET"
      );
      if (response?.status === 200 && response?.data?.success) {
        const invoiceDetailsArray = response.data?.data?.invoiceDetails;
        const invoiceDetails =
          Array.isArray(invoiceDetailsArray) && invoiceDetailsArray.length > 0
            ? invoiceDetailsArray[0]
            : null;
        setSelectedRedemption((prev) =>
          prev
            ? {
                ...prev,
                invoiceDetails,
                invoiceStatus: invoiceDetails?.Status ?? prev.invoiceStatus,
                price: invoiceDetails?.Total ?? prev.price,
              }
            : prev
        );
        showSuccessToast("Invoice refreshed from Xero.");
      } else {
        showWarningToast(response?.data?.message || "Could not refresh invoice from Xero.");
      }
    } catch (error) {
      showErrorToast(
        error?.response?.data?.message || "Could not refresh invoice from Xero."
      );
    } finally {
      setInvoiceRefreshLoading(false);
    }
  };

  const fetchRedemptionDetails = async () => {
    try {
      setLoading(true);
      const response = await api("v1/redemption/" + params.idRedemption, "GET", null);
      if (response && response.data && response.data.success) {
        // const vehicle = await api(`v1/vehicle/${response.data.data.redemption.vehicle}`, "GET");
        // const product = await api(`v1/product/${response.data.data.redemption.product}`, "GET");

        // Use imageUrl from API if available (backend resolves S3 key to signed URL)
        // Otherwise fall back to fetchFileFromCloud for backward compatibility
        const redemptionData = response.data.data.redemption;
        if (!redemptionData.imageUrl && redemptionData.image) {
          try {
            const imageKey = typeof redemptionData.image === "string" ? redemptionData.image : redemptionData.image?.key;
            if (imageKey && !imageKey.startsWith("http")) {
              const redemptionImage = await api(`v1/fetchFileFromCloud/url/${encodeURIComponent(imageKey)}`, "GET");
              redemptionData.imageUrl =
                redemptionImage?.data?.data?.url ?? null;
            }
          } catch (imageError) {
            console.warn("Failed to fetch redemption image:", imageError);
          }
        }
        redemptionData.image = redemptionData.imageUrl ?? null;

        // const merchantStore = response.data.data.redemption.merchantStore
        //   ? await api(`v1/merchantStore/${response.data.data.redemption.merchantStore}`, "GET")
        //   : null;
        // const merchantUser = response.data.data.redemption.merchantUser
        //   ? await api(`v1/merchantUser/${response.data.data.redemption.merchantUser}`, "GET")
        //   : null;
        let invoiceDetailsResponse = null;
        let invoiceDetails = null;

        if (response.data.data.redemption.invoiceID) {
          try {
            invoiceDetailsResponse = await api(
              `v1/redemption/${params.idRedemption}/invoiceDetails`,
              "GET"
            );

            // Check if response is successful
            if (invoiceDetailsResponse && invoiceDetailsResponse.status === 200) {
              const invoiceDetailsArray = invoiceDetailsResponse.data?.data?.invoiceDetails;
              if (Array.isArray(invoiceDetailsArray) && invoiceDetailsArray.length > 0) {
                invoiceDetails = invoiceDetailsArray[0];
              } else {
                invoiceDetails = null;
              }
            } else {
              // Handle error response (including 503) - silently log Xero auth issues
              const consentUrl =
                invoiceDetailsResponse?.data?.data?.consentUrl || invoiceDetailsResponse?.data?.consentUrl;

              if (consentUrl) {
                console.log("Xero re-authentication required. Consent URL available.");
              } else {
                console.log("Xero authentication issue detected.");
              }
            }
          } catch (error) {
            // Silently log if Xero authentication is needed
            if (error?.response?.data?.data?.consentUrl || error?.response?.data?.consentUrl) {
              console.log("Xero re-authentication required.");
            }
          }
        }

        // invoiceDetails && await api(`v1/redemption/${params.idRedemption}/updateInvoiceStatus`, "PATCH", { invoiceStatus: invoiceDetails.data.data.invoiceDetails[0].Status })

        // response.data.data.redemption.vehicle = vehicle.data.data.vehicle;
        // response.data.data.redemption.product = product.data.data.product;
        // response.data.data.redemption.merchantStore = merchantStore ? merchantStore.data.data.merchantStore : null;
        // response.data.data.redemption.merchantUser = merchantUser ? merchantUser.data.data.merchantUser : null;

        const redemptionRow = response.data.data.redemption;
        redemptionRow.invoiceDetails =
          invoiceDetails ||
          (redemptionRow.invoiceID
            ? {
                InvoiceID: redemptionRow.invoiceID,
                Status: redemptionRow.invoiceStatus || null,
                Total:
                  redemptionRow.price != null && redemptionRow.price !== ""
                    ? Number(redemptionRow.price)
                    : 0,
              }
            : null);

        setSelectedRedemption(redemptionRow);
        console.log(response.data.data.redemption);

        setLoading(false);
      } else {
        // If response is not successful, still set loading to false
        console.error("Failed to fetch redemption details:", response);
        setLoading(false);
      }
    } catch (error) {
      console.error("API error:", error);
      // Ensure loading is set to false even on error so page can render
      setLoading(false);
    }
  };

  // const getCurrentUser = async () => {
  //   const currUser = JSON.parse(sessionStorage.getItem("currentUser"));
  //   const response = await api("v1/user/" + currUser._id, "GET", null);
  // };

  useEffect(() => {
    // Check if we just completed Xero authentication
    const xeroAuth = searchParams.get("xeroAuth");
    if (xeroAuth === "success") {
      console.log("✅ Xero authentication successful! Token should now be valid.");
      // Remove the query parameter
      setSearchParams({});
      // Retry fetching invoice details after a short delay to ensure tokens are saved
      setTimeout(() => {
        console.log("Retrying invoice details fetch after successful Xero authentication...");
        fetchRedemptionDetails();
      }, 1000);
    } else if (xeroAuth === "error") {
      const errorMessage = searchParams.get("message");
      console.error("❌ Xero authentication failed:", errorMessage);
      setSearchParams({});
      fetchRedemptionDetails();
    } else {
      // Normal load
      fetchRedemptionDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteRedemptionConfirm = async () => {
    setLoading(true);
    try {
      const response = await api("v1/redemption/" + selectedRedemption._id, "DELETE", null);
      if (response.data.success) {
        window.location.replace("/redemption");
      }
    } catch (error) {
      console.error("API error:", error);
    }
    toggleDisplayDeleteConfirm();
    setLoading(false); //once api call is finished.
  };

  const toggleDisplayDeleteConfirm = () => {
    setDeleteConfirmationIsOpen(!deleteConfirmationIsOpen);
  };

  const editRedemption = () => {
    setEditRedemptionState((prevValue) => !prevValue);
  };

  const reloadRedemptionDetail = (updateRedemptionDetail) => {
    setEditRedemptionState((prevValue) => !prevValue);
    setSelectedRedemption(updateRedemptionDetail);
  };

  function formatDate(dateString) {
    const options = { year: "numeric", month: "long", day: "numeric" };
    const formattedDate = new Date(dateString).toLocaleDateString(undefined, options);

    return formattedDate;
  }

  const fetchVerifyPreview = useCallback(
    async (overrideVal) => {
      if (!selectedRedemption?._id) return;
      const isRefresh = verifyPreviewHasLoadedRef.current;
      if (isRefresh) {
        setBillingPreviewTotalsRefreshing(true);
      } else {
        setBillingPreviewInitialLoading(true);
        setBillingPreview(null);
      }
      setBillingPreviewError(null);
      try {
        let url = `v1/redemption/${selectedRedemption._id}/verify-preview?skipXeroContactCheck=1`;
        const trimmed =
          overrideVal != null && overrideVal !== undefined ? String(overrideVal).trim() : "";
        if (trimmed !== "") {
          const n = parseFloat(trimmed);
          if (!Number.isNaN(n) && n > 0) {
            url += `&billingUnitPreOverride=${encodeURIComponent(n)}`;
          }
        }
        const vt = String(billingVehicleTypeOverride || "").trim().toLowerCase();
        if (["sedan", "wagon", "4wd"].includes(vt)) {
          url += `&vehicleTypeOverride=${encodeURIComponent(vt)}`;
        }
        const response = await api(url, "GET", null);
        if (response?.data?.success && response?.data?.data) {
          setBillingPreview(response.data.data);
          verifyPreviewHasLoadedRef.current = true;
        } else {
          setBillingPreviewError(response?.data?.message || "Could not load billing amount.");
        }
      } catch (error) {
        console.error("Error fetching verify preview:", error);
        setBillingPreviewError(error?.response?.data?.message || "Could not load billing amount.");
      } finally {
        if (isRefresh) {
          setBillingPreviewTotalsRefreshing(false);
        } else {
          setBillingPreviewInitialLoading(false);
        }
      }
    },
    [selectedRedemption?._id, billingVehicleTypeOverride]
  );

  useEffect(() => {
    if (!verifyDialogOpen || !selectedRedemption?._id) return undefined;
    const delay = String(billingPreOverride).trim() === "" ? 0 : 400;
    const t = setTimeout(() => {
      fetchVerifyPreview(billingPreOverride);
    }, delay);
    return () => clearTimeout(t);
  }, [verifyDialogOpen, billingPreOverride, billingVehicleTypeOverride, selectedRedemption?._id, fetchVerifyPreview]);

  useEffect(() => {
    if (!isAdministratorPortal()) return undefined;
    let cancelled = false;
    const loadXeroCallStatus = async () => {
      try {
        const response = await api("v1/xeroapi/call-status", "GET");
        if (!cancelled && response?.data?.success) {
          const status = response.data.data || null;
          setXeroCallStatus(status);
          if (!parseFutureTimestamp(status?.rateLimitedUntil)) {
            setBillingPreviewError((prev) =>
              isXeroRateLimitMessage(prev) ? null : prev
            );
          }
        }
      } catch {
        if (!cancelled) setXeroCallStatus(null);
      }
    };
    loadXeroCallStatus();
    const timer = setInterval(loadXeroCallStatus, 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const untilMs = parseFutureTimestamp(xeroCallStatus?.rateLimitedUntil);
    if (untilMs == null) {
      setBillingPreviewError((prev) =>
        isXeroRateLimitMessage(prev) ? null : prev
      );
      return undefined;
    }
    const timer = setTimeout(() => {
      setBillingPreviewError((prev) =>
        isXeroRateLimitMessage(prev) ? null : prev
      );
    }, untilMs - Date.now() + 500);
    return () => clearTimeout(timer);
  }, [xeroCallStatus?.rateLimitedUntil]);

  const handleVerifyConfirm = async () => {
    try {
      setIsVerifiedLoading(true);
      const currentUser = JSON.parse(sessionStorage.getItem("currentUser") || "{}");
      const userEmail = currentUser?.email;
      const trimmed = String(billingPreOverride).trim();
      const payload = {
        idRedemption: selectedRedemption._id,
        status: true,
        userEmail: userEmail,
      };
      if (trimmed !== "") {
        const n = parseFloat(trimmed);
        if (!Number.isNaN(n) && n > 0) {
          payload.billingUnitAmountOverride = n;
        }
      }
      const vt = String(billingVehicleTypeOverride || "").trim().toLowerCase();
      if (["sedan", "wagon", "4wd"].includes(vt)) {
        payload.vehicleTypeOverride = vt;
      }
      const overrideReason = duplicateGuardOverrideReason.trim();
      if (overrideReason.length >= 10) {
        payload.duplicateGuardOverrideReason = overrideReason;
      }
      const response = await api(`v1/redemption/verify`, "POST", payload);
      if (response?.data?.success) {
        verifyPreviewHasLoadedRef.current = false;
        setBillingPreviewTotalsRefreshing(false);
        setBillingPreviewInitialLoading(false);
        setVerifyDialogOpen(false);
        setBillingPreview(null);
        setBillingPreviewError(null);
        setBillingPreOverride("");
        setBillingVehicleTypeOverride("");
        showSuccessToast(
          response.data.message ||
            "Redemption approved. Xero bill will be created on the next billing run."
        );
        await fetchRedemptionDetails();
      } else {
        const failMessage = verifyFailureMessage(response);
        setBillingPreviewError(failMessage);
        showErrorToast(failMessage);
      }
    } catch (error) {
      console.error("Error verifying redemption:", error);
      const failMessage = verifyFailureMessage(error?.response, error);
      setBillingPreviewError(failMessage);
      showErrorToast(failMessage);
    } finally {
      setIsVerifiedLoading(false);
    }
  };

  const openApproveDialog = () => {
    setBillingPreOverride("");
    setBillingVehicleTypeOverride("");
    setBillingPreviewError(null);
    setVerifyDialogOpen(true);
  };

  const handleCancelApproval = async () => {
    try {
      setIsVerifiedLoading(true);
      const currentUser = JSON.parse(sessionStorage.getItem("currentUser") || "{}");
      const response = await api(`v1/redemption/verify`, "POST", {
        idRedemption: selectedRedemption._id,
        status: false,
        userEmail: currentUser?.email,
      });
      if (response?.data?.success) {
        showSuccessToast(response.data.message || "Approval cancelled.");
        await fetchRedemptionDetails();
      } else {
        showErrorToast(response?.data?.message || "Could not cancel approval.");
      }
    } catch (error) {
      showErrorToast(error?.response?.data?.message || "Could not cancel approval.");
    } finally {
      setIsVerifiedLoading(false);
    }
  };

  const handleRetryBilling = async () => {
    try {
      setIsVerifiedLoading(true);
      const response = await api(`v1/redemption/${selectedRedemption._id}/retry-billing`, "POST");
      if (response?.data?.success) {
        showSuccessToast(response.data.message || "Queued for billing.");
        await fetchRedemptionDetails();
      } else {
        showErrorToast(response?.data?.message || "Could not queue billing.");
      }
    } catch (error) {
      showErrorToast(error?.response?.data?.message || "Could not queue billing.");
    } finally {
      setIsVerifiedLoading(false);
    }
  };

  const handleXeroResolved = async (nextCheck) => {
    if (nextCheck) setPageXeroCheck(nextCheck);
    if (nextCheck?.matches) setXeroResolveOpen(false);
    if (verifyDialogOpen) {
      await fetchVerifyPreview(billingPreOverride);
    } else {
      await fetchPageXeroCheck();
    }
  };

  const formatMoney = (n) =>
    n != null && !Number.isNaN(Number(n)) ? `$${Number(n).toFixed(2)}` : "—";

  const merchantAccountIdFromRedemption =
    selectedRedemption?.merchantStore?.merchantAccount?._id ||
    selectedRedemption?.merchantStore?.merchantAccount ||
    billingPreview?.merchantAccountId ||
    null;

  const activeXeroCheck = billingPreview?.xeroContactCheck || pageXeroCheck;

  const fetchPageXeroCheck = useCallback(async (merchantIdOverride) => {
    if (!isAdministratorPortal()) return;
    const merchantAccount =
      selectedRedemption?.merchantStore?.merchantAccount &&
      typeof selectedRedemption.merchantStore.merchantAccount === "object"
        ? selectedRedemption.merchantStore.merchantAccount
        : null;
    if (merchantAccount?.skipXeroContactCheck) {
      setPageXeroCheck({
        matches: true,
        reason: "SKIP_CHECK",
        skipped: true,
        mismatches: [],
      });
      return;
    }
    const merchantId =
      merchantIdOverride ||
      merchantAccount?._id ||
      selectedRedemption?.merchantStore?.merchantAccount;
    if (!merchantId) {
      if (!verifyDialogOpen) setPageXeroCheck(null);
      return;
    }
    setPageXeroCheckLoading(true);
    try {
      const response = await api(`v1/merchant/${merchantId}/xero-contact`, "GET");
      if (response?.data?.success) {
        setPageXeroCheck(response.data.data?.check || null);
      } else {
        setPageXeroCheck({
          matches: false,
          reason: "XERO_UNAVAILABLE",
          message: response?.data?.message || "Could not check the Xero contact for this merchant.",
          mismatches: [],
        });
      }
    } catch (error) {
      setPageXeroCheck({
        matches: false,
        reason: "XERO_UNAVAILABLE",
        message: error?.response?.data?.message || "Could not check the Xero contact for this merchant.",
        mismatches: [],
      });
    } finally {
      setPageXeroCheckLoading(false);
    }
  }, [selectedRedemption?._id, selectedRedemption?.merchantStore, verifyDialogOpen]);

  useEffect(() => {
    if (!verifyDialogOpen || !isAdministratorPortal()) {
      return;
    }
    // One check per open dialog. pageXeroCheck is in the dependency list so a
    // result stops the effect; fetching again here was a Xero call loop.
    if (billingPreview?.xeroContactCheck || pageXeroCheck) {
      return;
    }
    const merchantId =
      selectedRedemption?.merchantStore?.merchantAccount?._id ||
      selectedRedemption?.merchantStore?.merchantAccount ||
      billingPreview?.merchantAccountId;
    if (merchantId) fetchPageXeroCheck(merchantId);
  }, [verifyDialogOpen, billingPreview?.merchantAccountId, billingPreview?.xeroContactCheck, fetchPageXeroCheck, pageXeroCheck, selectedRedemption]);

  const handleImageFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError("");
    setUploadSuccess("");
    setUploadingImage(true);

    try {
      // Create FormData for image upload
      const formData = new FormData();
      formData.append("vehicleImage", file);

      // Update redemption with image using PATCH endpoint
      const response = await api(
        `v1/redemption/${selectedRedemption._id}`,
        "PATCH",
        formData,
        true // useFormData
      );

      console.log("Upload response:", response);
      
      if (response?.data?.success) {
        setUploadSuccess("Image uploaded successfully!");
        // Refresh redemption details to get updated image
        await fetchRedemptionDetails();
        // Close modal after 2 seconds
        setTimeout(() => {
          setUploadImageModalOpen(false);
          setUploadSuccess("");
        }, 2000);
      } else {
        const errorMessage = response?.data?.message || response?.data?.details || "Failed to upload image";
        console.error("Upload failed:", errorMessage, response?.data);
        setUploadError(errorMessage);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      const errorMessage = 
        error?.response?.data?.details || 
        error?.response?.data?.message || 
        error?.message ||
        "An error occurred while uploading the image. Please try again.";
      setUploadError(errorMessage);
      
      // Log full error for debugging
      if (error?.response) {
        console.error("Full error response:", error.response);
      }
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Check if redemption needs image upload (pending status and no image for merchants, or just missing image for admins)
  const isAdmin = sessionStorage.getItem("selectedPortal") === "Administrator";
  const isMerchant = !isAdmin;
  const hasNoImage = selectedRedemption?.image === null || selectedRedemption?.image === undefined || selectedRedemption?.image === "";
  const isPending = selectedRedemption?.status === "pending";
  
  // For merchants: show button only if pending and no image
  // For admins: show button if no image (regardless of status)
  // Coffee Pass redemptions never carry a vehicle photo.
  const isCoffeePassRedemption = selectedRedemption?.vertical === "cafe";
  const needsImageUpload =
    !isCoffeePassRedemption && hasNoImage && (isAdmin || (isMerchant && isPending));

  return (
    selectedRedemption && (
      <div className="page partner-user-single w-full flex flex-column items-center min-h-h-screen p-4 gap-8">
        {loading && <GlobalLoadingSpinner />}
        <div className="pageTitle flex flex-col text-center sm:w-full sm:flex-row sm:justify-between">
          <h1>Redemption Details</h1>
          <div>
            <IconButton onClick={() => navigate(-1)} sx={{ border: "1px solid grey" }}>
              <ArrowBackIcon />
            </IconButton>
          </div>
        </div>
        <div className="flex w-full justify-start">
          {sessionStorage.getItem("selectedPortal") == "Administrator" &&
            (isCoffeePassRedemption &&
            resolveBillingVerificationStatus(selectedRedemption) === "unverified" ? (
              <Alert severity="info" className="w-full max-w-[580px]">
                Coffee Pass redemption: there is no vehicle photo to approve. Publish its Xero bill from{" "}
                <a href="/coffee-pass-bills" className="underline">
                  Coffee Pass Bills
                </a>
                .
              </Alert>
            ) : (
              <RedemptionVerificationPanel
                redemption={selectedRedemption}
                loading={isVerifiedLoading}
                onApprove={openApproveDialog}
                onCancelApproval={handleCancelApproval}
                onRetryBilling={handleRetryBilling}
              />
            ))}
        </div>
        {isAdministratorPortal() && xeroCallBannerMessage(xeroCallStatus) && (
          <div className="w-full max-w-[580px]">
            <Alert severity={xeroCallStatus?.atConcurrentLimit ? "warning" : "info"}>
              {xeroCallBannerMessage(xeroCallStatus)}
            </Alert>
          </div>
        )}
        {sessionStorage.getItem("selectedPortal") == "Administrator" &&
          !selectedRedemption.invoiceID &&
          resolveBillingVerificationStatus(selectedRedemption) === "unverified" && (
          <div className="w-full max-w-[580px]">
            <XeroContactMismatchAlert
              check={activeXeroCheck}
              loading={pageXeroCheckLoading && !verifyDialogOpen}
              merchantId={merchantAccountIdFromRedemption}
              onFix={() => setXeroResolveOpen(true)}
              onAccepted={handleXeroResolved}
            />
          </div>
        )}
        {/* Redemption Single Content */}
        <div className="w-full">
          <div
            className={`
                        vehicle-single
                        max-w-[580px]
                        rounded-lg
                        ${DATA_GRID_STYLES}
                    `}
          >
            {/* <h2>{selectedRedemption.name}</h2> */}

            <span>Product</span>
            <span>
              <a href="javascript:void(0);" onClick={() => navigate("/product/" + selectedRedemption.product._id)}>
                <span>
                  Name: <strong> {selectedRedemption.product.name}</strong>
                </span>
                <br />
                <span>
                  Type: <strong> {selectedRedemption.product.type}</strong>
                </span>
                <br />
                <span>
                  Duration: <strong> {selectedRedemption.product.subscriptionDuration} month(s)</strong>
                </span>
              </a>
            </span>

            <span>
              Vehicle
              {selectedRedemption.image ? (
                <div>
                  <ModalImage small={selectedRedemption.image} large={selectedRedemption.image} alt="Vehicle Image" />
                  {/* Show upload button even when image exists, for admins to replace image */}
                  {isAdmin && (
                    <div className="mt-2">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => setUploadImageModalOpen(true)}
                        sx={{ borderColor: COLORS.priorityBlue.hex, color: COLORS.priorityBlue.hex }}
                      >
                        Replace Image
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center">
                  <p className="text-gray-500 mb-2">No image available</p>
                  {needsImageUpload && (
                    <Button
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => setUploadImageModalOpen(true)}
                      sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                    >
                      Upload Image
                    </Button>
                  )}
                </div>
              )}
            </span>

            <span>
              <a
                href="javascript:void(0);"
                onClick={() =>
                  selectedRedemption.vehicle?._id ? navigate("/vehicle/" + selectedRedemption.vehicle._id) : null
                }
              >
                <strong>Vehicle: </strong>
              </a>
              <div className="infoCardText">
                {selectedRedemption.vehicle ? (
                  <>
                    Rego: <strong> {selectedRedemption.vehicle?.rego || "N/A"} </strong>
                    <br />
                    Vin: <strong> {selectedRedemption.vehicle?.vin || "N/A"}</strong>
                    <br />
                    Make: <strong>{selectedRedemption.vehicle?.make || "N/A"} </strong>
                    <br />
                    Model: <strong> {selectedRedemption.vehicle?.model || "N/A"}</strong>
                    <br />
                    Model Year: <strong>{selectedRedemption.vehicle?.modelYear || "N/A"} </strong>
                    <br />
                    Color: <strong> {selectedRedemption.vehicle?.color || "N/A"}</strong>
                    <br />
                    Age: <strong>{selectedRedemption.vehicle?.age || "N/A"} </strong>
                  </>
                ) : (
                  <em>No vehicle details available.</em>
                )}
              </div>
            </span>
            {/* <p>
                                <strong> <a href={"/vehicle/" + selectedRedemption.vehicle._id}> {selectedRedemption.vehicle.rego}  ({selectedRedemption.vehicle.vin}) </a> </strong>
                                </p> */}

            {selectedRedemption.merchantStore && (
              <>
                <span>Merchant Store </span>
                <span>
                  <a
                    href="javascript:void(0);"
                    onClick={() =>
                      navigate(
                        "/merchant/" +
                          selectedRedemption.merchantStore.merchantAccount._id +
                          "/store/" +
                          selectedRedemption.merchantStore._id
                      )
                    }
                  >
                    <strong> {selectedRedemption.merchantStore.name} </strong>
                  </a>
                </span>
              </>
            )}

            {selectedRedemption.merchantUser && (
              <>
                <span>Merchant User </span>
                <span>
                  <a
                    href="javascript:void(0);"
                    onClick={() =>
                      navigate(
                        "/merchant/" +
                          selectedRedemption.merchantUser.merchantAccount +
                          "/user/" +
                          selectedRedemption.merchantUser._id
                      )
                    }
                  >
                    <strong>
                      {" "}
                      {selectedRedemption.merchantUser.firstName +
                        " " +
                        selectedRedemption.merchantUser.lastName +
                        " (" +
                        selectedRedemption.merchantUser.email +
                        ")"}{" "}
                    </strong>
                  </a>
                </span>
              </>
            )}
            <span>Redemption Date (UTC) </span>
            <span>{formatDate(selectedRedemption.redemptionDateUTC)}</span>
            {/* <div className="flex flex-nowrap items-center justify-between w-full border-b-[2px]">
                            <p>Redemptions: </p>
                            <ul className="font-bold">
                                {selectedRedemption.redemptions && selectedRedemption.redemptions.map((val, index) => { return <li>{formatDate(val)}</li> })}
                            </ul>
                        </div> */}

            {/* <div className="flex flex-nowrap items-center justify-between w-full border-b-[2px]">
                            <p>Created On: </p>
                            <p> <strong> {formatDate(selectedRedemption.createdOnUTC)} </strong></p>
                        </div> */}
            <span>Status </span>
            <span>
              <strong>
                {selectedRedemption.status === "pending" ? (
                  <span className="text-orange-600">Pending - Image Upload Required</span>
                ) : (
                  <span className="text-green-600">Completed</span>
                )}
              </strong>
            </span>
            <span>Active </span>
            <span>{selectedRedemption.active ? "Yes" : "No"}</span>
            <span>Invoice </span>
            <span>
              {(() => {
                const invoiceId = selectedRedemption.invoiceDetails?.InvoiceID ?? selectedRedemption.invoiceID ?? "";
                const xeroBillUrl = invoiceId
                  ? `https://go.xero.com/AccountsPayable/View.aspx?InvoiceID=${encodeURIComponent(invoiceId)}`
                  : null;
                return invoiceId ? (
                  <a
                    href={xeroBillUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Click to view on Xero
                  </a>
                ) : (
                  ""
                );
              })()}
            </span>
            <span>Invoice Status </span>
            <span>{selectedRedemption.invoiceDetails?.Status ?? selectedRedemption.invoiceStatus ?? "Verify to Generate Bill"}</span>
            <span>Invoice Amount </span>
            <span>${selectedRedemption.invoiceDetails?.Total ?? selectedRedemption.price ?? "0"}</span>
          </div>

          {sessionStorage.getItem("selectedPortal") == "Administrator" &&
            selectedRedemption.invoiceID && (
              <div className="px-4 pb-2">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={invoiceRefreshLoading}
                  onClick={refreshInvoiceFromXero}
                >
                  {invoiceRefreshLoading ? "Refreshing from Xero…" : "Refresh invoice from Xero"}
                </Button>
              </div>
            )}

          {sessionStorage.getItem("selectedPortal") == "Administrator" && (
            <div>
              <div className="actionItems flex gap-4">
                <Button
                  className="self-start m-4"
                  sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                  variant="contained"
                  onClick={editRedemption}
                >
                  Edit Redemption
                </Button>
                <Button
                  className="self-start bg-red-600 m-4"
                  variant="contained"
                  color="error"
                  onClick={() => {
                    toggleDisplayDeleteConfirm(true);
                  }}
                >
                  Delete Redemption
                </Button>
              </div>
            </div>
          )}

          {/* Upload Image Button for Merchants when redemption is pending, or for Admins when image is missing */}
          {needsImageUpload && (
            <div className="mt-4">
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => setUploadImageModalOpen(true)}
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                size="large"
              >
                {isAdmin ? "Upload Vehicle Image" : "Upload Vehicle Image"}
              </Button>
            </div>
          )}
        </div>

        {/* // Edit Modal HTML */}

        <ModalPopup
          showModal={editRedemptionState}
          handleShowModal={editRedemption}
          popupTitle={"Edit Redemption"}
          popupContent={
            <RedemptionForm
              selectedRedemption={selectedRedemption}
              callback={reloadRedemptionDetail}
              closeModal={editRedemption}
            />
          }
          cancelButtonText=""
          confirmButtonText=""
          confirmCallbackAction=""
        />

        {/* Verify Redemption – billing confirmation */}
        <ModalPopup
          showModal={verifyDialogOpen}
          maxWidth="md"
          handleShowModal={() => {
            if (!isVerifiedLoading) {
              verifyPreviewHasLoadedRef.current = false;
              setBillingPreviewTotalsRefreshing(false);
              setBillingPreviewInitialLoading(false);
              setVerifyDialogOpen(false);
              setBillingPreview(null);
              setBillingPreviewError(null);
              setBillingPreOverride("");
              setBillingVehicleTypeOverride("");
              setDuplicateGuardOverrideReason("");
            }
          }}
          popupTitle="Confirm verification"
          popupContent={
            <div className="flex flex-col gap-4">
              <p className="text-gray-700">
                Approving queues a Xero bill for the next billing run (every 30 minutes). The merchant is emailed
                after the bill is created in Xero.
              </p>
              {isAdministratorPortal() && xeroCallBannerMessage(xeroCallStatus) && (
                <Alert severity={xeroCallStatus?.atConcurrentLimit ? "warning" : "info"}>
                  {xeroCallBannerMessage(xeroCallStatus)}
                </Alert>
              )}
              {billingPreviewInitialLoading && (
                <div className="flex items-center gap-2">
                  <CircularProgress size={20} />
                  <span>Loading billing amount…</span>
                </div>
              )}
              {billingPreviewError && !billingPreviewInitialLoading && (
                <p className="text-amber-700 text-sm">{billingPreviewError}</p>
              )}
              {billingPreview && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                  {(billingPreview.pricingMismatch || billingPreview.requiresVehicleTypeSelection) && (
                    <>
                      <div className="text-sm text-gray-800 space-y-1 border-b border-gray-200 pb-2">
                        <p>
                          <span className="text-gray-600">Product:</span>{" "}
                          <strong>{billingPreview.productName || "—"}</strong>
                        </p>
                        <p>
                          <span className="text-gray-600">Store:</span>{" "}
                          <strong>{billingPreview.merchantStoreName || "—"}</strong>
                          {" · "}
                          <span className="text-gray-600">Merchant:</span>{" "}
                          <strong>{billingPreview.merchantName || "—"}</strong>
                        </p>
                        <p className="text-gray-600 text-xs leading-snug">{billingPreview.tierDescription}</p>
                        {billingPreview.storePricing?.hasRow ? (
                          <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                            <p>
                              Store row — base: {formatMoney(billingPreview.storePricing.basePrice)}
                              {billingPreview.storePricing.variantSedan != null &&
                                ` · Sedan/Hatch: ${formatMoney(billingPreview.storePricing.variantSedan)}`}
                              {billingPreview.storePricing.variantWagon != null &&
                                ` · SUV/Wagon: ${formatMoney(billingPreview.storePricing.variantWagon)}`}
                              {billingPreview.storePricing.variant4wd != null &&
                                ` · 4WD/7-seat: ${formatMoney(billingPreview.storePricing.variant4wd)}`}
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-600 mt-1">
                            No store pricing row — catalog price {formatMoney(billingPreview.storePricing?.catalogPrice)}
                          </p>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-xs">
                          <p className="bg-white/80 rounded px-2 py-1 border border-gray-100">
                            <span className="text-gray-600 block">Store table (pre-promotion)</span>
                            <strong className="text-base">{formatMoney(billingPreview.storeTableUnitPrePromotion)}</strong>
                          </p>
                          <p className="bg-white/80 rounded px-2 py-1 border border-gray-100">
                            <span className="text-gray-600 block">On redemption (snapshot)</span>
                            <strong className="text-base">
                              {formatMoney(billingPreview.redemptionStoredBillingUnitAmount)}
                            </strong>
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                  {billingPreview.pricingMismatch && (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      Stored billing unit on the redemption does not match the current store pricing. Enter the correct
                      pre-promotion amount below before confirming, or leave blank to use the default calculation shown
                      in the totals.
                    </Alert>
                  )}
                  {(billingPreview.requiresVehicleTypeSelection ||
                    VALID_VEHICLE_TYPES.includes(String(billingVehicleTypeOverride || "").toLowerCase()) ||
                    VALID_VEHICLE_TYPES.includes(String(billingPreview.resolvedVehicleType || "").toLowerCase())) && (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      Vehicle type is missing for this variant-priced product. Select the correct type to recalculate
                      billing. This selection is saved to the vehicle for future redemptions.
                    </Alert>
                  )}
                  {(billingPreview.requiresVehicleTypeSelection ||
                    VALID_VEHICLE_TYPES.includes(String(billingVehicleTypeOverride || "").toLowerCase()) ||
                    VALID_VEHICLE_TYPES.includes(String(billingPreview.resolvedVehicleType || "").toLowerCase())) && (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Vehicle type (required)"
                      value={
                        billingVehicleTypeOverride ||
                        (VALID_VEHICLE_TYPES.includes(
                          String(billingPreview.resolvedVehicleType || "").toLowerCase()
                        )
                          ? String(billingPreview.resolvedVehicleType).toLowerCase()
                          : "")
                      }
                      onChange={(e) => setBillingVehicleTypeOverride(e.target.value)}
                      helperText="Required before confirmation."
                    >
                      {(billingPreview.availableVehicleTypes || VALID_VEHICLE_TYPES).map((vt) => (
                        <MenuItem key={vt} value={vt}>
                          {vt === "sedan"
                            ? "Sedan/Hatch"
                            : vt === "wagon"
                              ? "SUV/Wagon"
                              : "4WD/7Seater"}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {billingPreview.pricingMismatch && (
                    <TextField
                      fullWidth
                      size="small"
                      label="Override billing unit (before promotions)"
                      value={billingPreOverride}
                      onChange={(e) => setBillingPreOverride(e.target.value)}
                      placeholder={
                        billingPreview.storeTableUnitPrePromotion != null
                          ? `e.g. ${Number(billingPreview.storeTableUnitPrePromotion).toFixed(2)}`
                          : ""
                      }
                      helperText="Leave empty to use the calculated default. Typing a value updates the preview."
                    />
                  )}
                  {billingPreviewTotalsRefreshing && (
                    <div className="flex items-center gap-2 text-gray-500 text-sm -mt-1">
                      <CircularProgress size={14} thickness={5} />
                      <span>Updating totals…</span>
                    </div>
                  )}
                  {billingPreview.subscriptionAmount != null && billingPreview.adminFeeAmount != null && (
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        Subscription line (contract rate, before admin fee)
                        {billingPreview.productName ? ` (${billingPreview.productName})` : ""}:{" "}
                        <strong>{formatMoney(billingPreview.subscriptionAmount)}</strong>
                      </p>
                      <p>
                        Admin fee{billingPreview.adminFeePercent != null ? ` (${billingPreview.adminFeePercent}%)` : ""}:{" "}
                        <strong className="text-red-600">−{formatMoney(billingPreview.adminFeeAmount)}</strong>
                      </p>
                      <p className="pt-2 border-t border-gray-200 mt-2 text-gray-800 font-medium">
                        {billingPreview.merchantName} will be billed{" "}
                        <strong>{formatMoney(billingPreview.amountToBill)}</strong>
                      </p>
                    </div>
                  )}
                  {(billingPreview.subscriptionAmount == null || billingPreview.adminFeeAmount == null) && (
                    <p className="font-semibold text-gray-800">
                      {billingPreview.merchantName} will be billed{" "}
                      <strong>{formatMoney(billingPreview.amountToBill)}</strong>
                      {billingPreview.productName && ` for ${billingPreview.productName}`}.
                    </p>
                  )}
                </div>
              )}
              {isVerifiedLoading && (
                <div className="flex items-center gap-2 text-blue-700">
                  <CircularProgress size={20} />
                  <span>Approving…</span>
                </div>
              )}
              {isAdministratorPortal() && (
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Duplicate guard override reason (admin only)"
                  value={duplicateGuardOverrideReason}
                  onChange={(e) => setDuplicateGuardOverrideReason(e.target.value)}
                  helperText="Required only if verification is blocked by the 24-hour or same-visit duplicate rules (min. 10 characters)."
                />
              )}
              <p className="text-gray-600 text-sm">Do you want to continue?</p>
            </div>
          }
          cancelButtonText="Cancel"
          confirmButtonText="Confirm verify"
          confirmDisabled={
            isVerifiedLoading ||
            billingPreviewInitialLoading ||
            billingPreviewTotalsRefreshing ||
            !!(
              billingPreview?.requiresVehicleTypeSelection &&
              !VALID_VEHICLE_TYPES.includes(
                String(billingVehicleTypeOverride || "").toLowerCase()
              )
            )
          }
          confirmCallbackAction={handleVerifyConfirm}
        />

        {isAdministratorPortal() && (
          <XeroContactResolveDialog
            open={xeroResolveOpen}
            onClose={() => setXeroResolveOpen(false)}
            merchantId={merchantAccountIdFromRedemption}
            check={activeXeroCheck}
            onResolved={handleXeroResolved}
          />
        )}

        {/* Delete Redemption Confimation Modal */}
        <ModalPopup
          showModal={deleteConfirmationIsOpen}
          handleShowModal={toggleDisplayDeleteConfirm}
          popupTitle={"Delete Redemption"}
          popupContent={
            <div>
              Are you sure you want to delete this redemption for Vehicle
              <strong>
                <em>
                  {selectedRedemption.vehicle?.rego
                    ? `${selectedRedemption.vehicle.rego} ( Product: ${selectedRedemption.product?.name || "N/A"})`
                    : `Product: ${selectedRedemption.product?.name || "N/A"}`}
                </em>
              </strong>
              ?
            </div>
          }
          cancelButtonText={"Cancel"}
          confirmButtonText={"Delete"}
          confirmCallbackAction={handleDeleteRedemptionConfirm}
        />

        {/* Upload Image Modal for Merchants */}
        <ModalPopup
          showModal={uploadImageModalOpen}
          handleShowModal={() => {
            setUploadImageModalOpen(false);
            setUploadError("");
            setUploadSuccess("");
          }}
          popupTitle={"Upload Vehicle Image"}
          popupContent={
            <div className="p-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageFileSelect}
                style={{ display: "none" }}
                disabled={uploadingImage}
              />
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Please take or select a photo of the vehicle. The image will be uploaded and the redemption status will be updated to completed.
                </p>
                
                {uploadError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{uploadError}</p>
                  </div>
                )}
                
                {uploadSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">{uploadSuccess}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-center">
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleUploadButtonClick}
                  disabled={uploadingImage}
                  sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                  size="large"
                >
                  {uploadingImage ? "Uploading..." : "Select & Upload Image"}
                </Button>
              </div>

              {uploadingImage && (
                <div className="mt-4 flex justify-center">
                  <CircularProgress />
                </div>
              )}
            </div>
          }
          cancelButtonText={"Close"}
          confirmButtonText={""}
          confirmCallbackAction={""}
        />
      </div>
    )
  );
};

export default RedemptionSingle;
